import { useContext } from "react";
import AppContext from "../context/AppContext";

/**
 * Permissions that unlock Casino FM V3 upload affordances (Replace / Clear /
 * Upload buttons across Games, Providers, and Collections).
 *
 * The OR semantics mirror the backend's "legacy parent permission" fallback:
 * roles that only hold the umbrella `casino.configuration.casino_fm` keep
 * their existing upload ability even though the granular `casino.configuration.uploads`
 * permission is missing from their payload. Source of truth: PAMENG-2009 BE PR.
 */
const CASINO_FM_V3_UPLOAD_PERMISSIONS = [
  "casino.configuration.casino_fm",
  "casino.configuration.uploads",
] as const;

type AppContextShape = {
  adminPermissions?: string[] | null;
};

/**
 * Returns `true` when the current admin's role holds any permission that
 * authorizes Casino FM V3 image uploads. Consumed by the Games, Providers,
 * and Collections configuration pages to gate the upload UI affordances.
 */
export function useCanUploadCasinoFmV3Assets(): boolean {
  const { adminPermissions } = useContext(AppContext) as AppContextShape;
  return (
    Array.isArray(adminPermissions) &&
    CASINO_FM_V3_UPLOAD_PERMISSIONS.some((p) => adminPermissions.includes(p))
  );
}

export { CASINO_FM_V3_UPLOAD_PERMISSIONS };
